/**
 * Root landing for the CEO watchlist (2026-10-23).
 *
 * Anyone holding the `ceo` grant (only the admin by default) opens the bare
 * app root `/` on the CEO page instead of the Products grid, on desktop only:
 * phones and tablets (picking-touch viewports) keep landing on Picking
 * (Rhys 2026-10-23); `/ceo` typed directly still opens there. Everyone else
 * keeps today's behaviour, and the tailnet root keeps landing on Local
 * Deliveries (remoteAccess.tailnetRootLandsOnDeliveries owns that rule).
 *
 * Pure helpers so App.js routing stays testable without rendering the app.
 */
import { CEO_PATH, isProductsPath, normalizePathname } from './moduleRoutes';

/** localStorage flag: "this browser's account held the ceo grant last time". */
export const CEO_LANDING_STORAGE_KEY = 'warehouse:ceo-landing';

/**
 * Strict grant check. hasModulePermission in App.js treats unknown keys and
 * "not loaded yet" as allowed; CEO is admin-granted, so here a missing key
 * means no.
 */
export function hasCeoGrant(permissions) {
  if (!permissions || typeof permissions !== 'object') return false;
  if (permissions.isAdmin === true) return true;
  return permissions.modulePermissions?.ceo === true;
}

/**
 * A `?sku=` on the root is a (legacy) Products share link, not a plain visit
 * of the app: it must still open those products (2026-10-23).
 */
function isSkuShareLink(search) {
  try {
    return new URLSearchParams(String(search || '')).has('sku');
  } catch (_) {
    return false;
  }
}

/** window.localStorage, or null where it is missing or blocked. */
export function getCeoLandingStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

/**
 * Should this visit to the root open the CEO page?
 * `rawPath` is the path BEFORE ensureCanonicalUrl rewrites `/` to `/products`
 * (`/products` itself is an explicit choice and still opens Products).
 * `pickingTouch` (phone or tablet viewport) keeps the root on Picking.
 */
export function rootLandsOnCeo({ rawPath, search, hasCeo, remoteRestricted, tailnetRoot, pickingTouch } = {}) {
  if (pickingTouch === true) return false;
  if (normalizePathname(rawPath) !== '/') return false;
  if (isSkuShareLink(search)) return false;
  if (hasCeo !== true) return false;
  if (remoteRestricted === true) return false;
  if (tailnetRoot === true) return false;
  return true;
}

/**
 * Is this handleRoute run a visit of the root? Either the URL is still the
 * bare `/`, or the first load was `/` and the first routing pass (before
 * permissions landed) already rewrote it to `/products`, or to `/picking` on
 * a picking-touch device. `initialRootVisit` is one-shot: App.js spends it on
 * the first run after permissions land, so a later re-run never pulls anyone
 * off a `/products` they chose.
 */
export function isRootLandingVisit({ rawPath, path, initialRootVisit } = {}) {
  if (normalizePathname(rawPath) === '/') return true;
  if (initialRootVisit !== true) return false;
  return isProductsPath(path) || normalizePathname(path) === '/picking';
}

/**
 * Should the first paint already show CEO? Only when the URL is the root (or
 * /ceo itself) and the last permission load for this browser held the grant,
 * so Rhys does not see the Products grid flash on every load. If the grant
 * was revoked since, App.js bounces home once permissions land. Never on a
 * picking-touch viewport: there the root belongs to Picking, and a typed
 * `/ceo` is opened by normal routing instead.
 */
export function shouldSeedCeoLanding({ rawPath, search, storage, pickingTouch } = {}) {
  if (pickingTouch === true) return false;
  const path = normalizePathname(rawPath);
  if (path !== '/' && path !== CEO_PATH) return false;
  if (path === '/' && isSkuShareLink(search)) return false;
  try {
    return Boolean(storage) && storage.getItem(CEO_LANDING_STORAGE_KEY) === '1';
  } catch (_) {
    // Private mode / blocked storage: no seed, normal routing decides.
    return false;
  }
}

/** Remember (or forget) the grant after `my-permissions` resolves. */
export function rememberCeoLanding(storage, hasCeo) {
  if (!storage) return;
  try {
    if (hasCeo) storage.setItem(CEO_LANDING_STORAGE_KEY, '1');
    else storage.removeItem(CEO_LANDING_STORAGE_KEY);
  } catch (_) {
    // Storage full or blocked: the seed is a nicety, routing still works.
  }
}
